import { z } from "zod";
import { PropSpec, PropType } from "./props";
import { Shape } from "./shape";

// D70/D82 — Behavior-Descriptor (das QUARTETT) + Placeable-Katalog (Prototyp). Der zentrale, engine- und
// domänen-NEUTRALE Vertrag, an dem beide Erweiterungs-Achsen andocken (Editor-Typen × Engines).
// Aus EINER Behavior-Deklaration speisen sich DREI Konsumenten:
//   props   → Editor-Details-Formular  | Blockly-Value-Blöcke      | Runtime-Init-Parameter
//   states  → —                        | Blockly-Bedingungs-Blöcke | Runtime-Laufzeit-Zustand
//   events  → —                        | Blockly „Wenn …"-Blöcke   | Runtime feuert Events
//   actions → —                        | Blockly „Dann …"-Blöcke   | Runtime-Methoden (per binding)

/** Dependency-freier IconRef-Spiegel (strukturell = contracts `IconRef`), als Zod für Manifest-Validierung. */
const IconRefSchema = z.object({ name: z.string() });

/** Ein lesbarer Laufzeit-Zustand eines Behaviors → speist Blockly-Bedingungs-Blöcke (D71). */
export const BehaviorState = z.object({
  id: z.string().min(1),
  type: PropType,
  label: z.string().optional(),
});
export type BehaviorState = z.infer<typeof BehaviorState>;

/** Eine empfangbare Action eines Behaviors → speist Blockly „Dann …"-Blöcke (D71). */
export const BehaviorAction = z.object({
  id: z.string().min(1),
  label: z.string().optional(),
  params: z.record(z.string(), PropSpec).optional(),
});
export type BehaviorAction = z.infer<typeof BehaviorAction>;

/** Woher ein Descriptor stammt: Built-in (Editor-Typ-Paket, eager) vs. Projekt (Engine-Extraktor, Manifest). */
export const DescriptorSource = z.enum(["builtin", "project"]);
export type DescriptorSource = z.infer<typeof DescriptorSource>;

export const BehaviorDescriptor = z.object({
  /** by-id-Bindung (immer eindeutig), z.B. "moveable-platform". */
  id: z.string().min(1),
  label: z.string().min(1),
  /** Gruppierung im Tools-Panel (z.B. "platforms"). */
  category: z.string().optional(),
  icon: IconRefSchema.optional(),
  /** Konfigurierbare Werte → Details-Formular + Value-Blöcke + Init-Parameter. */
  props: z.record(z.string(), PropSpec).default({}),
  /** Lesbare Laufzeit-Zustände → Bedingungs-Blöcke. */
  states: z.array(BehaviorState).default([]),
  /** Gefeuerte Events (id-Liste) → „Wenn …"-Hat-Blöcke. */
  events: z.array(z.string()).default([]),
  /** Empfangene Actions → „Dann …"-Befehls-Blöcke. */
  actions: z.array(BehaviorAction).default([]),
  /** Viewport-Hinweis (z.B. "path" / "region"); vom Editor-Renderer interpretiert (D75). */
  editor: z.object({ gizmo: z.string().optional() }).optional(),
  source: DescriptorSource,
  /** Natives Symbol (engine-spezifisch, "Modulpfad#Export"); vom Extraktor (D73) gesetzt. */
  binding: z.string().optional(),
});
export type BehaviorDescriptor = z.infer<typeof BehaviorDescriptor>;

/** Default-Größe (Welt-Einheiten) — bei `sprite` die Bildgröße, bei `region` das Rechteck. */
const Size = z.object({ w: z.number().positive(), h: z.number().positive() });

/**
 * Wie ein Placeable im Editor dargestellt wird (D82). DISKRIMINANT `kind`:
 *  - `sprite`: HAT eine Grafik (das frühere „Asset") — `image` Pflicht.
 *  - `region`: GRAFIKLOS — Rechteck-Gizmo (z.B. „Boost Area"/Trigger-Zone), `color`/`size` optional.
 *  - `point`:  GRAFIKLOS — Punkt/Marker (z.B. Spawn), `icon` optional.
 */
export const PlaceableView = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("sprite"),
    /** Pfad zum Bild relativ zum Projekt-Root (z.B. "assets/ice_platform.png"). */
    image: z.string().min(1),
    size: Size.optional(),
  }),
  z.object({
    kind: z.literal("region"),
    /** CSS-Farbe des Rechteck-Gizmos (z.B. "#22d3ee"); fehlt = Editor-Default. */
    color: z.string().optional(),
    size: Size.optional(),
  }),
  z.object({
    kind: z.literal("point"),
    icon: IconRefSchema.optional(),
  }),
]);
export type PlaceableView = z.infer<typeof PlaceableView>;

// Placeable-Katalog (Prototyp, D70/D82). Ein im Editor PLATZIERBARES Ding — MIT Grafik (`view.kind:"sprite"`,
// = das frühere „Asset") ODER GRAFIKLOS (`view.kind:"region"`/`"point"`, z.B. eine „Boost Area" / ein Spawn-
// Marker — im Code/TOML deklariert, im Viewport als Gizmo gerendert). Die platzierte INSTANZ lebt im Level-TDoc
// (D75) mit Overrides; Auflösung = merge(placeable.defaults, instance.overrides) → Details zeigt gemergt,
// schreibt nur Abweichungen.
export const PlaceableDescriptor = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** Editor-Darstellung: Sprite (Grafik) oder grafiklos (Region/Punkt-Gizmo). */
  view: PlaceableView,
  /** Welche BehaviorDescriptor-ids dieses Placeable trägt. */
  behaviors: z.array(z.string()).default([]),
  /** Placeable-weite Default-Props, key = "behaviorId.propName". */
  defaults: z.record(z.string(), z.unknown()).optional(),
  /**
   * Collision-/Hitbox-Form (D85), anker-zentriert, in QUELL-BILD-PIXELN (Textur-Raum). Pro-Placeable (alle
   * Instanzen teilen sie). Bei Sprites im Editor automatisch aus der Alpha-Außenkontur abgeleitet ODER je
   * Placeable editiert. SSOT = `*.placeable.toml` (additiv-optional ⇒ Manifest bleibt schemaVersion 2).
   * KOORDINATENRAUM: die Phaser-Runtime legt das Sprite in Texturauflösung an und setzt den Body in Textur-px
   * ⇒ Bild-Pixel sind genau richtig (skaliert mit dem Sprite). Der EDITOR zeichnet das Sprite in eine kleine
   * `cell×scale`-Anzeige-Box und skaliert die Overlay-Kontur entsprechend herunter (Bild-px → Box). NB: die
   * pro-INSTANZ-Form `PlatformerInstance.shape` (Area-Werkzeug) ist dagegen in WELT-Einheiten — anderer Kontext,
   * dasselbe `Shape`-Primitiv. Runtime-Mapping best-effort: Matter = formtreu; Arcade = AABB/Kreis, Polygon → BBox.
   */
  collision: Shape.optional(),
  /** Cloud-Import-Provenance (D74) → knüpft an §9.4 source_job_id. */
  provenance: z
    .object({ sourceJobId: z.string().optional(), importedAt: z.string().optional() })
    .optional(),
});
export type PlaceableDescriptor = z.infer<typeof PlaceableDescriptor>;

/** Bildpfad eines Placeables, falls es ein Sprite ist (sonst undefined — grafiklose Region/Punkt). */
export function placeableImage(p: PlaceableDescriptor): string | undefined {
  return p.view.kind === "sprite" ? p.view.image : undefined;
}
